import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  computeTotals,
  formatBps,
  formatCents,
  lineTotal,
  parseCents,
  type DiscountType,
  type LineItemInput,
  type PriceBookItem,
} from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { Button, Input, Select, cx } from './ui';

/**
 * Line item editor, shared by jobs, estimates and invoices.
 *
 * The running total shown here is computed with the same `computeTotals` the
 * API uses, so what the office sees while typing is what the invoice will say.
 * No second formula, no "close enough" preview.
 */

export interface LineItemEditorProps {
  value: LineItemInput[];
  onChange: (items: LineItemInput[]) => void;
  taxRateBps: number;
  discount?: {
    type: DiscountType;
    valueCents: number;
    valueBps: number;
    onChange: (next: { type: DiscountType; valueCents: number; valueBps: number }) => void;
  };
  amountPaidCents?: number;
  readOnly?: boolean;
  /**
   * Leave the grand total out. Used where the card carries a summary band of
   * its own — the figure belongs in exactly one place on a page.
   */
  hideTotal?: boolean;
}

export function LineItemEditor({
  value,
  onChange,
  taxRateBps,
  discount,
  amountPaidCents = 0,
  readOnly,
  hideTotal,
}: LineItemEditorProps) {
  const [picker, setPicker] = useState('');

  const { data: priceBook = [] } = useQuery({
    queryKey: keys.priceBook(),
    queryFn: () => api.priceBook.list(),
    select: (d) => d.items,
    staleTime: 5 * 60_000,
  });

  const totals = useMemo(
    () =>
      computeTotals({
        lineItems: value.map((li) => ({
          quantity: li.quantity,
          unitPriceCents: li.unitPriceCents,
          taxable: li.taxable,
        })),
        taxRateBps,
        discountType: discount?.type ?? 'none',
        discountValueCents: discount?.valueCents ?? 0,
        discountValueBps: discount?.valueBps ?? 0,
        payments: amountPaidCents ? [{ amountCents: amountPaidCents }] : [],
      }),
    [value, taxRateBps, discount, amountPaidCents],
  );

  function addFromPriceBook(id: string) {
    const item = priceBook.find((p: PriceBookItem) => p.id === id);
    if (!item) return;
    onChange([
      ...value,
      {
        priceBookItemId: item.id,
        name: item.name,
        description: item.description,
        kind: item.kind,
        quantity: 1,
        unitPriceCents: item.unitPriceCents,
        taxable: item.taxable,
        position: value.length,
      },
    ]);
    setPicker('');
  }

  function addBlank() {
    onChange([
      ...value,
      {
        priceBookItemId: null,
        name: '',
        description: null,
        kind: 'labor',
        quantity: 1,
        unitPriceCents: 0,
        taxable: false,
        position: value.length,
      },
    ]);
  }

  function patch(index: number, next: Partial<LineItemInput>) {
    onChange(value.map((li, i) => (i === index ? { ...li, ...next } : li)));
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index).map((li, i) => ({ ...li, position: i })));
  }

  return (
    <div>
      <div className={cx('overflow-x-auto', !readOnly && 'px-[21px] pt-[21px]')}>
        <table className="w-full border-collapse text-[13px] tabular-nums">
          <thead>
            <tr>
              <Th className={readOnly ? 'pl-[21px]' : undefined}>Description</Th>
              <Th align="right" className="w-[86px]">Qty</Th>
              <Th align="right" className="w-[118px]">Rate</Th>
              <Th align="center" className="w-[60px]">Tax</Th>
              <Th align="right" className={cx('w-[110px]', readOnly && 'pr-[21px]')}>Amount</Th>
              {!readOnly && <Th className="w-[36px]" />}
            </tr>
          </thead>
          <tbody>
            {value.length === 0 && (
              <tr>
                <td colSpan={readOnly ? 5 : 6} className="px-3 py-8 text-center text-[13px] text-ink-4">
                  No line items yet.
                </td>
              </tr>
            )}

            {value.map((item, index) => (
              <tr key={index} className="border-b border-line last:border-b-0">
                <td className={cx('pr-3', readOnly ? 'py-[22px] pl-[21px]' : 'py-1.5')}>
                  {readOnly ? (
                    <div>
                      <div className="text-[15px] font-semibold text-ink">{item.name}</div>
                      {item.description && (
                        <div className="mt-1 text-[13px] text-ink-3">{item.description}</div>
                      )}
                      {/* What kind of line it is, in the row rather than a column of its own. */}
                      <div className="mt-1 text-[13px] text-ink-4 capitalize">{item.kind}</div>
                    </div>
                  ) : (
                    <Input
                      value={item.name}
                      placeholder="Description"
                      onChange={(e) => patch(index, { name: e.target.value })}
                      className="h-8"
                    />
                  )}
                </td>
                <td className={cx('pr-2', readOnly ? 'py-[22px]' : 'py-1.5')}>
                  {readOnly ? (
                    <div className="text-right tnum">{item.quantity}</div>
                  ) : (
                    <Input
                      type="number"
                      min={0}
                      step={0.25}
                      value={item.quantity}
                      onChange={(e) =>
                        patch(index, { quantity: Math.max(Number(e.target.value) || 0, 0) })
                      }
                      className="h-8 text-right tnum"
                    />
                  )}
                </td>
                <td className={cx('pr-2', readOnly ? 'py-[22px]' : 'py-1.5')}>
                  {readOnly ? (
                    <div className="text-right tnum">{formatCents(item.unitPriceCents)}</div>
                  ) : (
                    <MoneyInput
                      cents={item.unitPriceCents}
                      onCommit={(cents) => patch(index, { unitPriceCents: cents })}
                    />
                  )}
                </td>
                <td className={cx('text-center', readOnly ? 'py-[22px]' : 'py-1.5')}>
                  {/*
                   * Read-only, this is a fact about the line, not a control:
                   * a greyed-out checkbox reads as "you may not change this",
                   * which is a different sentence from "this line is taxed".
                   */}
                  {readOnly ? (
                    item.taxable ? (
                      <span
                        className="inline-grid size-[17px] place-items-center rounded-[4px] bg-accent-soft text-accent ring-1 ring-accent-line ring-inset"
                        title="Taxable"
                      >
                        <svg viewBox="0 0 20 20" fill="none" className="size-[11px]" aria-hidden>
                          <path
                            d="M4.5 10.5 8 14l7.5-8"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="sr-only">Taxable</span>
                      </span>
                    ) : (
                      <span className="text-ink-4" title="Not taxed">
                        —
                      </span>
                    )
                  ) : (
                    <input
                      type="checkbox"
                      checked={item.taxable}
                      onChange={(e) => patch(index, { taxable: e.target.checked })}
                      className="size-4 accent-accent"
                      aria-label="Taxable"
                    />
                  )}
                </td>
                <td
                  className={cx(
                    'pr-2 text-right font-semibold tnum',
                    readOnly ? 'py-[22px] pr-[21px]' : 'py-1.5',
                  )}
                >
                  {formatCents(lineTotal(item.unitPriceCents, item.quantity))}
                </td>
                {!readOnly && (
                  <td className="py-1.5 text-right">
                    <button
                      onClick={() => remove(index)}
                      className="grid size-7 place-items-center rounded-md text-ink-4 hover:bg-danger-soft hover:text-danger"
                      aria-label={`Remove ${item.name || 'line item'}`}
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select
            value={picker}
            onChange={(e) => addFromPriceBook(e.target.value)}
            className="h-8 max-w-[280px] text-[13px]"
          >
            <option value="">Add from price book…</option>
            {priceBook.map((item: PriceBookItem) => (
              <option key={item.id} value={item.id}>
                {item.name} — {formatCents(item.unitPriceCents)}
              </option>
            ))}
          </Select>
          <Button size="sm" onClick={addBlank}>
            Add custom line
          </Button>
        </div>
      )}

      <div className={cx('flex justify-end', readOnly ? 'px-[21px] pt-4 pb-5' : 'mt-5')}>
        <dl className="w-[340px] space-y-1 text-[14px]">
          <Row label="Subtotal" value={formatCents(totals.subtotalCents)} />

          {discount && !readOnly && (
            <div className="flex items-center justify-between gap-2 py-1">
              <div className="flex items-center gap-1.5">
                <Select
                  value={discount.type}
                  onChange={(e) =>
                    discount.onChange({
                      type: e.target.value as DiscountType,
                      valueCents: 0,
                      valueBps: 0,
                    })
                  }
                  className="h-7 w-[104px] text-[12.5px]"
                >
                  <option value="none">No discount</option>
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed</option>
                </Select>
                {discount.type === 'percent' && (
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={discount.valueBps / 100}
                    onChange={(e) =>
                      discount.onChange({
                        type: 'percent',
                        valueCents: 0,
                        valueBps: Math.round(Number(e.target.value) * 100),
                      })
                    }
                    className="h-7 w-[68px] text-right text-[12.5px] tnum"
                  />
                )}
                {discount.type === 'fixed' && (
                  <MoneyInput
                    cents={discount.valueCents}
                    className="h-7 w-[88px] text-[12.5px]"
                    onCommit={(cents) =>
                      discount.onChange({ type: 'fixed', valueCents: cents, valueBps: 0 })
                    }
                  />
                )}
              </div>
              <span className="tnum text-ink-2">
                {totals.discountCents > 0 ? `−${formatCents(totals.discountCents)}` : '—'}
              </span>
            </div>
          )}

          {discount && readOnly && totals.discountCents > 0 && (
            <Row
              label={`Discount${discount.type === 'percent' ? ` (${formatBps(discount.valueBps)})` : ''}`}
              value={`−${formatCents(totals.discountCents)}`}
            />
          )}

          {/* Only when something on the document is actually taxable — a
              labour-only ticket should not show a $0.00 tax line. */}
          {taxRateBps > 0 && totals.taxableSubtotalCents > 0 && (
            <Row
              label={`Tax ${formatBps(taxRateBps)} on ${formatCents(totals.taxableBaseCents)}`}
              value={formatCents(totals.taxCents)}
            />
          )}

          {!hideTotal && (
            <div className="mt-2 flex items-center justify-between border-t border-line-2 pt-3 font-display text-[19px] font-bold tracking-[-0.025em]">
              <dt>Total</dt>
              <dd className="tnum">{formatCents(totals.totalCents)}</dd>
            </div>
          )}

          {amountPaidCents > 0 && (
            <>
              <Row label="Paid" value={`−${formatCents(totals.amountPaidCents)}`} />
              <div className="flex items-center justify-between text-[15px] font-semibold">
                <dt>{totals.balanceDueCents < 0 ? 'Credit' : 'Balance due'}</dt>
                <dd className={cx('tnum', totals.balanceDueCents > 0 ? 'text-danger' : 'text-good')}>
                  {formatCents(Math.abs(totals.balanceDueCents))}
                </dd>
              </div>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-ink-3">{label}</dt>
      <dd className="tnum">{value}</dd>
    </div>
  );
}

function Th({
  children,
  align = 'left',
  className,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  return (
    <th
      className={cx(
        'border-b border-line px-0 pb-2.5 text-[12px] font-medium text-ink-3',
        align === 'right' && 'pr-2 text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  );
}

/**
 * Money input. Holds a string while focused so a half-typed "12." does not get
 * mangled, and converts to integer cents exactly once, on blur.
 */
function MoneyInput({
  cents,
  onCommit,
  className,
}: {
  cents: number;
  onCommit: (cents: number) => void;
  className?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <Input
      value={draft ?? (cents / 100).toFixed(2)}
      onFocus={() => setDraft((cents / 100).toFixed(2))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) onCommit(parseCents(draft) ?? cents);
        setDraft(null);
      }}
      inputMode="decimal"
      className={cx('h-8 text-right tnum', className)}
    />
  );
}
